/**
 * The ways to bring an AI account, as onboarding words them. One source for the AI account card, the notice on the
 * AI account step and the team proposal, the reminder email's guide and the chat refusal of a pasted key (#2150), so
 * the screen, the email and the chat cannot drift apart when a provider is added.
 *
 * Claude, ChatGPT and SuperGrok plans first, Claude's pre-selected (#1719), then the four keys the API accepts
 * (#1716). Each choice carries its #1719 limit line (plans) and its #1722 sentence on what the user pays and to whom.
 */
/** The card's id for each plan to sign in with. */
export type AiAccountPlanId = 'claude' | 'chatgpt' | 'supergrok'

/** The API's provider id for each key, in the order the API lists them (`AI_ACCOUNT_PROVIDERS`). */
export type AiAccountKeyId = 'anthropic' | 'openai' | 'xai' | 'openrouter'

/**
 * One choice. `Id` defaults to any string so a made-up list still fits; the shared list itself narrows it to the
 * plan and key ids above, so a consumer such as the card checks those ids at compile time without a cast.
 */
export interface AiAccountChoice<Id extends string = string> {
    /** The card's option id: `claude`, `chatgpt` or `supergrok` for a plan, the API's provider id for a key. */
    id: Id
    kind: 'plan' | 'key'
    /** What the option is called on the card. */
    label: string
    /** The provider as running copy names it: Claude, ChatGPT, SuperGrok for a plan; Anthropic, OpenAI, xAI, OpenRouter for a key. */
    name: string
    /** Who sells it and bills for it. */
    vendor: string
    /** What to know about the option, shown under its label. */
    hint: string
    /** The one plain sentence saying what the user will pay and to whom. */
    billing: string
    /** Where to sign up for a key (no scheme), null for a plan. */
    site: string | null
}

/**
 * The line under a plan (#1719): it runs on the plan's own limits, and the provider may limit or block the
 * connection. Claude's reads, word for word, "Uses your Claude plan's limits. Anthropic may limit or block this
 * connection; you can switch to an API key any time."
 */
function planHint(name: string, vendor: string): string {
    return `Uses your ${name} plan's limits. ${vendor} may limit or block this connection; you can switch to an API key any time.`
}

/**
 * The billing line under a plan (#1722): the plan is what is billed, by the provider that sells it. It makes no
 * per-use or "no extra charge" promise, since use counts against the plan's limits on the provider's own terms.
 */
function planBilling(name: string, vendor: string): string {
    return `Your team's AI use comes out of your ${name} plan, which ${vendor} bills you for.`
}

/** The billing line under a key (#1722): the provider bills the use on that key directly, at its own prices. */
function keyBilling(vendor: string): string {
    return `${vendor} bills you directly for your team's AI use on this key, at ${vendor}'s prices.`
}

const GROK = 'Grok'

/**
 * What a key's provider is known for, where its own name does not say it: an xAI key is for Grok. The xAI key's hint
 * and the reminder email's sign-up step both say it from here, so a Grok user can tell which provider is theirs.
 * Keyed by the choice's id.
 */
export const AI_ACCOUNT_KEY_FOR: Readonly<Record<string, string>> = { xai: GROK }

function plan(id: AiAccountPlanId, label: string, name: string, vendor: string): AiAccountChoice<AiAccountPlanId> {
    return {
        id,
        kind: 'plan',
        label,
        name,
        vendor,
        hint: planHint(name, vendor),
        billing: planBilling(name, vendor),
        site: null,
    }
}

function key(
    id: AiAccountKeyId,
    label: string,
    name: string,
    hint: string,
    site: string,
): AiAccountChoice<AiAccountKeyId> {
    return { id, kind: 'key', label, name, vendor: name, hint, billing: keyBilling(name), site }
}

export const AI_ACCOUNT_CHOICES: readonly AiAccountChoice<AiAccountPlanId | AiAccountKeyId>[] = [
    plan('claude', 'Connect Claude Pro/Max', 'Claude', 'Anthropic'),
    plan('chatgpt', 'Connect ChatGPT plan', 'ChatGPT', 'OpenAI'),
    plan('supergrok', 'Connect SuperGrok plan', 'SuperGrok', 'xAI'),
    key(
        'anthropic',
        'Anthropic API key',
        'Anthropic',
        'From the API keys page of your Anthropic Console.',
        'console.anthropic.com',
    ),
    key(
        'openai',
        'OpenAI API key',
        'OpenAI',
        'From the API keys page of your OpenAI Platform account.',
        'platform.openai.com',
    ),
    key('xai', 'xAI API key', 'xAI', `From the API keys page of your xAI Console, for ${GROK}.`, 'console.x.ai'),
    key(
        'openrouter',
        'OpenRouter key',
        'OpenRouter',
        'From the Keys page of your OpenRouter account.',
        'openrouter.ai',
    ),
]

/** "A", "A or B", "A, B or C": how running copy lists choices, in the order given. */
export function orList(words: readonly string[]): string {
    if (words.length <= 1) {
        return words[0] ?? ''
    }
    return `${words.slice(0, -1).join(', ')} or ${words.at(-1)}`
}

/** What the person reads when a message carrying an AI provider key is stopped, in the portal and at the API. */
export const PROVIDER_KEY_IN_CHAT_TEXT =
    'That looks like an AI provider key. Keys never go in chat: connect it on the AI account card instead.'
